/**
 * App σερβιτόρου — αποκλειστικά για κινητό.
 *
 * Ξεχωριστό από την κονσόλα ταμία (DineInOperations) επειδή είναι άλλο
 * εργαλείο: ο σερβιτόρος στέκεται όρθιος με ένα χέρι, ο ταμίας κάθεται
 * μπροστά σε οθόνη αφής. Κοινά κρατάμε ό,τι έχει σημασία να μη διαφωνεί:
 * services/dineInApi (endpoints) και utils/dineInMoney (ποσά, εκπτώσεις).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Utensils, Bell, User, RefreshCw, LogOut, Users, Loader2, Wifi, WifiOff, Settings, StickyNote, ChevronRight,
  CalendarDays, Star, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useSocket } from '../../../hooks/useSocket';
import { usePermissions } from '../../../hooks/usePermissions';
import { useToast } from '../../shared/Toast';
import { useTheme } from '../../../contexts/ThemeContext';
import { productsAPI } from '../../../services/api';
import { dineInApi } from '../../../services/dineInApi';
import { FloorTone, floorTone } from '../../../utils/dineInFloor';
import { allergiesOf, athensMinutes, floorReservations, FloorReservation } from '../../../utils/dineInReservations';
import { kitchenMessagePresets } from '../../../utils/kitchenMessages';
import type { Product, Category } from '../../../types/order';
import { withSellableSizes } from '../../../utils/sellableSizes';
import { useDailyDishesToday } from '../../../hooks/useDailyDishesToday';
import { applyDailyDishes } from '../../../utils/dailyDishes';
import { WaiterStyles, EmptyState, Button, IconButton, money } from './ui';
import DeviceSettingsSheet from './DeviceSettingsSheet';
import { TABLE_CARD, TABLE_GRID, useWaiterPrefs } from './devicePrefs';
import TableSheet from './TableSheet';
import OrderScreen from './OrderScreen';
import { OrderDraft, draftFitsSession, readOrderDrafts } from './orderDraft';

type Tab = 'tables' | 'alerts' | 'profile';

interface Alert {
  id: string;
  message: string;
  at: Date;
}

/** Χρώμα κάρτας ανά κατάσταση τραπεζιού — ο σερβιτόρος διαβάζει την αίθουσα
 *  με μια ματιά, χωρίς να διαβάζει λέξεις. */
const TABLE_TONE: Record<FloorTone, string> = {
  free: 'bg-surface-1 border-emerald-600/70',
  occupied: 'bg-red-100 dark:bg-red-800/70 border-red-600',
  bill: 'bg-yellow-100 dark:bg-yellow-500/30 border-yellow-400',
  reserved: 'bg-surface-1 border-blue-500'
};
/** Νέα εμφάνιση (12/10/2026): όπως η κάτοψη του ταμείου — απαλό φόντο + χρωματιστό περίγραμμα,
 *  όχι γεμάτα κόκκινα/κίτρινα. Κλάση xv2-table (ΟΧΙ role-table, που ξαναβάφει με μπάρα πάνω). */
const TABLE_TONE_V2: Record<FloorTone, string> = {
  free: 'bg-surface-2 border-emerald-500/60',
  occupied: 'bg-red-50 border-red-500 dark:bg-red-500/15 dark:border-red-400',
  bill: 'bg-amber-50 border-amber-500 dark:bg-amber-400/15 dark:border-amber-400',
  reserved: 'bg-brand-soft border-brand'
};

const MENU_MAX_AGE_MS = 5 * 60 * 1000;

const WaiterApp: React.FC = () => {
  const { store, employee, employeeLogout } = useAuth();
  const { socket, isConnected } = useSocket();
  const { hasPermission } = usePermissions();
  const { showToast } = useToast();
  const { look } = useTheme();
  const v2 = look === 'v2';

  const [tab, setTab] = useState<Tab>('tables');
  const [tables, setTables] = useState<any[]>([]);
  const [zoneFilter, setZoneFilter] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  // Xentrix Sala: οι σημερινές κρατήσεις, για «κρατημένο» και για ποιος κάθεται.
  const [reservations, setReservations] = useState<FloorReservation[]>([]);
  const salaOn = settings?.sala_enabled === true;
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unread, setUnread] = useState(0);

  const [summary, setSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [openTable, setOpenTable] = useState<any>(null);
  // Ρυθμίσεις συσκευής (γρανάζι): θέμα, στήλες/μεγέθη, προϊόντα ως κάρτες ή λίστα — μόνο σε αυτό το κινητό.
  const prefs = useWaiterPrefs();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [ordering, setOrdering] = useState<{ sessionId: number | null; guestCount: number } | null>(null);
  // Παραγγελίες που δεν στάλθηκαν (refresh, κλείσιμο εφαρμογής…) — ξαναδιαβάζονται όταν κλείνει η οθόνη
  // παραγγελίας. Βλ. orderDraft.
  const [drafts, setDrafts] = useState<OrderDraft[]>([]);
  useEffect(() => {
    if (store?.id && !ordering) setDrafts(readOrderDrafts(store.id));
  }, [store?.id, ordering]);

  /** Ποιο τραπέζι είναι ανοιχτό — σε ref για να το βλέπει ο socket handler
   *  χωρίς να ξαναδένεται σε κάθε αλλαγή. */
  const openTableIdRef = useRef<number | null>(null);
  useEffect(() => {
    openTableIdRef.current = openTable?.id ?? null;
  }, [openTable]);

  const loadTables = useCallback(async () => {
    if (!store?.id) return;
    try {
      const data = await dineInApi.myTables(store.id);
      setTables(data.tables || []);
    } catch (error) {
      console.error('Failed to load tables:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [store?.id]);

  /** Πότε φορτώθηκε ο κατάλογος: το κινητό μένει ανοιχτό όλη τη βάρδια, ενώ οι τιμές μπορεί να
   *  αλλάξουν στο μεταξύ — και με το κατώτατο ο server κόβει γύρο με παλιά τιμή. */
  const menuLoadedAtRef = useRef(0);
  const loadMenu = useCallback(() => {
    if (!store?.id) return;
    menuLoadedAtRef.current = Date.now();
    Promise.all([productsAPI.getCategories(store.id), productsAPI.getProducts(store.id)])
      .then(([cats, prods]) => {
        setCategories(cats || []);
        setProducts(withSellableSizes(prods || [])); // χωρίς τα κρυμμένα μεγέθη
      })
      .catch((error) => {
        menuLoadedAtRef.current = 0; // να ξαναδοκιμάσει στο επόμενο άνοιγμα παραγγελίας
        console.error('Failed to load menu:', error);
      });
  }, [store?.id]);

  useEffect(() => {
    if (!store?.id) return;
    loadTables();
    dineInApi.settings(store.id).then(setSettings).catch(() => setSettings({}));
    loadMenu();
  }, [store?.id, loadTables, loadMenu]);

  // «Πιάτα ημέρας» (migration 278): η κατηγορία πρώτη και ΜΟΝΟ τα σημερινά στην οθόνη παραγγελίας. Η πλήρης
  // λίστα μένει για το τραπέζι και τις γραμμές του καλαθιού (ένα πιάτο που βγήκε από τα σημερινά δεν χάνεται
  // από παραγγελία που ήδη γράφεται). Αλλαγή από ταμείο/διαχείριση (socket) → φρέσκος κατάλογος.
  const { today: dailyToday, reload: reloadDaily } = useDailyDishesToday(store?.id, store?.daily_dishes_enabled === true, loadMenu);
  const dailyCatalog = useMemo(() => applyDailyDishes(categories, products, dailyToday), [categories, products, dailyToday]);

  // Νέα παραγγελία με κατάλογο παλιότερο από MENU_MAX_AGE_MS → φρέσκος κατάλογος (και τα σημερινά πιάτα).
  const startOrdering = useCallback((sessionId: number | null, guestCount: number) => {
    if (Date.now() - menuLoadedAtRef.current > MENU_MAX_AGE_MS) {
      loadMenu();
      if (store?.daily_dishes_enabled === true || dailyToday.enabled) reloadDaily();
    }
    setOrdering({ sessionId, guestCount });
  }, [loadMenu, reloadDaily, store?.daily_dishes_enabled, dailyToday.enabled]);

  const loadReservations = useCallback(() => {
    if (!store?.id || !salaOn) return;
    dineInApi.reservationsToday(store.id).then(list => setReservations(Array.isArray(list) ? list : [])).catch(() => {});
  }, [store?.id, salaOn]);
  useEffect(() => { loadReservations(); }, [loadReservations]);

  // Ανανέωση κάθε 30΄΄ — τα σύνολα των τραπεζιών αλλάζουν από το ταμείο
  useEffect(() => {
    const timer = setInterval(() => { loadTables(); loadReservations(); }, 30000);
    return () => clearInterval(timer);
  }, [loadTables, loadReservations]);

  useEffect(() => {
    if (!socket || !salaOn) return;
    const onReservation = () => loadReservations();
    socket.on('reservation:changed', onReservation);
    return () => { socket.off('reservation:changed', onReservation); };
  }, [socket, salaOn, loadReservations]);

  useEffect(() => {
    if (tab !== 'profile' || !store?.id) return;
    setSummaryLoading(true);
    dineInApi
      .mySummary(store.id)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [tab, store?.id]);

  const pushAlert = useCallback((message: string) => {
    setAlerts((prev) => [
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, message, at: new Date() },
      ...prev,
    ].slice(0, 50));
    setUnread((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!socket) return;

    const onTableUpdate = (data: any) => {
      if (data?.type === 'waiter_call') {
        const message = `Κάλεσμα από την κουζίνα${data.table_number ? ` — τραπέζι ${data.table_number}` : ''}`;
        showToast('info', message);
        pushAlert(message);
      }
      loadTables();
      if (openTableIdRef.current && data?.table_id === openTableIdRef.current) {
        setRefreshSignal((n) => n + 1);
      }
    };

    const onOrderReady = (data: { orderId: number; tableNumber: string }) => {
      const message = `Έτοιμη παραγγελία — τραπέζι ${data.tableNumber}`;
      showToast('info', message);
      pushAlert(message);
      loadTables();
      setRefreshSignal((n) => n + 1);
    };

    socket.on('table-update', onTableUpdate);
    socket.on('order-ready', onOrderReady);
    return () => {
      socket.off('table-update', onTableUpdate);
      socket.off('order-ready', onOrderReady);
    };
  }, [socket, loadTables, pushAlert, showToast]);

  const zones: Array<{ id: number; name: string }> = [];
  tables.forEach((t) => {
    if (!zones.some((z) => z.id === t.zone_id)) {
      zones.push({ id: t.zone_id, name: t.zone_name || '' });
    }
  });
  const shownTables = zoneFilter === null ? tables : tables.filter((t) => t.zone_id === zoneFilter);
  const tableReservations = floorReservations(reservations, athensMinutes());

  /** Πρόχειρα που ισχύουν ακόμη: το τραπέζι υπάρχει και δεν έκλεισε/ξανάνοιξε από τότε. */
  const pendingDrafts = drafts.filter((draft) => {
    const table = tables.find((t) => t.id === draft.tableId);
    return !!table && draftFitsSession(draft, table.current_session_id ?? null);
  });
  const draftTableIds = new Set(pendingDrafts.map((draft) => draft.tableId));

  /** Ένα πάτημα στο «δεν στάλθηκαν»: κατευθείαν στην παραγγελία του τραπεζιού, όπου ξαναβγαίνει το
   *  πρόχειρο (ίδια συνεδρία — βλ. orderDraft). */
  const resumeDraft = (draft: OrderDraft) => {
    const table = tables.find((t) => t.id === draft.tableId);
    if (!table) return;
    const sessionId: number | null = table.current_session_id ?? null;
    setOpenTable(table);
    // Όπως η «Νέα παραγγελία»: κατάλογος παλιότερος από MENU_MAX_AGE_MS → φρέσκος (Φ5 — το κατώτατο
    // του server κόβει γύρο με παλιά τιμή).
    startOrdering(sessionId, sessionId ? table.guest_count ?? draft.guestCount : draft.guestCount);
  };

  const tabs: Array<{ key: Tab; icon: React.ElementType; label: string; badge?: number }> = [
    { key: 'tables', icon: Utensils, label: 'Τραπέζια' },
    { key: 'alerts', icon: Bell, label: 'Ειδοποιήσεις', badge: unread },
    { key: 'profile', icon: User, label: 'Προφίλ' },
  ];

  return (
    /* ΣΤΑΘΕΡΟ ύψος (h-screen), όχι min-h: έτσι κυλά το <main> και όχι η σελίδα. Με min-h-screen το main
       μεγάλωνε όσο τα τραπέζια (70 στο GENERIS = 4344px), κυλούσε η σελίδα, και το overscroll «contain»
       του main έκοβε το σύρσιμο με ένα δάχτυλο πάνω στη λίστα — δούλευε μόνο με δύο (7/10/2026). */
    <div className="h-screen bg-surface-0 flex flex-col">
      <WaiterStyles />

      <header className="role-header bg-surface-1 border-b border-line px-3 py-2 flex items-center justify-between wa-safe-top">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-t1 leading-tight truncate">{store?.name}</h1>
          <p className="mt-0.5 text-sm text-t3 leading-tight truncate">
            {employee?.first_name} {employee?.last_name}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <IconButton label="Ρυθμίσεις συσκευής" onClick={() => setSettingsOpen(true)} className="bg-surface-2 active:bg-surface-3">
            <Settings className="w-6 h-6" />
          </IconButton>
          {isConnected ? (
            <Wifi className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-600 dark:text-red-400 animate-pulse" />
          )}
          <button
            onClick={() => { setRefreshing(true); loadTables(); }}
            aria-label="Ανανέωση"
            className="w-[44px] h-[44px] rounded-xl bg-surface-2 flex items-center justify-center active:bg-surface-3"
          >
            <RefreshCw className={`w-6 h-6 ${refreshing ? 'animate-spin text-blue-600 dark:text-blue-400' : 'text-t2'}`} />
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto wa-scroll wa-main-pad">
        {tab === 'tables' && (
          <>
            {/* Παραγγελίες που δεν στάλθηκαν (refresh κατά λάθος, κλείσιμο εφαρμογής, αποσύνδεση) */}
            {pendingDrafts.length > 0 && (
              <div className="px-3 pt-2 space-y-2">
                {pendingDrafts.map((draft) => {
                  const count = draft.lines.reduce((sum: number, l: any) => sum + (Number(l?.quantity) || 0), 0);
                  return (
                    <button
                      key={draft.tableId}
                      type="button"
                      onClick={() => resumeDraft(draft)}
                      className="w-full min-h-[56px] px-3 py-2 rounded-2xl bg-amber-100 dark:bg-amber-500/20 border-2 border-amber-400 text-left flex items-center gap-3 active:scale-[0.99] transition-transform"
                    >
                      <StickyNote className="w-6 h-6 shrink-0 text-amber-600 dark:text-amber-400" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-bold text-t1 leading-tight">
                          Τραπέζι {draft.tableNumber}: {count} {count === 1 ? 'είδος δεν στάλθηκε' : 'είδη δεν στάλθηκαν'}
                        </span>
                        <span className="block text-sm text-t3 leading-tight mt-0.5">Πάτα για να συνεχίσεις την παραγγελία</span>
                      </span>
                      <ChevronRight className="w-5 h-5 shrink-0 text-t3" />
                    </button>
                  );
                })}
              </div>
            )}

            {zones.length > 1 && (
              <div className="px-3 pt-2 flex gap-2 overflow-x-auto">
                <button aria-pressed={zoneFilter === null}
                  onClick={() => setZoneFilter(null)}
                  className={`role-choice shrink-0 px-4 min-h-[44px] rounded-full text-sm font-bold ${
                    zoneFilter === null ? 'bg-cyan-600 text-white' : 'bg-surface-1 text-t2'
                  }`}
                >
                  Όλα
                </button>
                {zones.map((zone) => (
                  <button aria-pressed={zoneFilter === zone.id}
                    key={zone.id}
                    onClick={() => setZoneFilter(zone.id)}
                    className={`role-choice shrink-0 px-4 min-h-[44px] rounded-full text-sm font-bold ${
                      zoneFilter === zone.id ? 'bg-cyan-600 text-white' : 'bg-surface-1 text-t2'
                    }`}
                    type="button"
                  >
                    {zone.name}
                  </button>
                ))}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
              </div>
            ) : shownTables.length === 0 ? (
              <EmptyState icon={Utensils} title="Δεν υπάρχουν τραπέζια" />
            ) : (
              /* Τραπέζια ανά σειρά από το γρανάζι (2/3/4): όσο στενεύει η κάρτα, ο αριθμός πάνω και το
                 ποσό από κάτω· στις 4 στήλες χωρίς ζώνη/άτομα — χωρούν (π.χ. 24 της «ΠΙΣΩ» σε μία οθόνη). */
              <div data-testid="tables-grid" className={`p-3 grid ${TABLE_GRID[prefs.tableColumns]} gap-2`}>
                {shownTables.map((table) => {
                  const card = TABLE_CARD[prefs.tableColumns];
                  const tr = tableReservations.get(table.id);
                  const next = tr?.next;
                  const seated = table.current_session_id ? tr?.seated : undefined;
                  const seatedAllergies = seated ? allergiesOf(seated) : '';
                  const tone = floorTone(table.status, !!next);
                  return (
                    <button data-status={table.status}
                      key={table.id}
                      onClick={() => setOpenTable(table)}
                      data-tone={tone}
                      aria-label={[`Τραπέζι ${table.table_number}`, seated?.customer_name, seatedAllergies && `Αλλεργίες: ${seatedAllergies}`].filter(Boolean).join(', ')}
                      className={`${v2 ? 'xv2-table' : 'role-table'} relative ${card.minH} rounded-2xl border-2 ${card.pad} text-left active:scale-[0.98] transition-transform ${
                        v2 ? TABLE_TONE_V2[tone] : TABLE_TONE[tone]
                      }`}
                    >
                      {(seated?.guest_is_vip || seatedAllergies) && (
                        <span className="absolute top-1.5 right-1.5 flex items-center gap-0.5" data-testid="table-reservation-flags">
                          {seated?.guest_is_vip && <Star className="w-4 h-4 text-amber-500 fill-amber-400" aria-label="VIP" />}
                          {seatedAllergies && <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" aria-label="Αλλεργίες" />}
                        </span>
                      )}
                      {draftTableIds.has(table.id) && (
                        <StickyNote
                          aria-label="Παραγγελία που δεν στάλθηκε"
                          className="absolute bottom-1.5 right-1.5 w-5 h-5 text-amber-500"
                        />
                      )}
                      <div className={card.stacked ? 'flex flex-col gap-0.5' : 'flex items-baseline justify-between gap-2'}>
                        <span className={`${card.number} font-bold text-t1 leading-none`}>
                          {table.table_number}
                        </span>
                        {table.current_session_id ? (
                          <span className={`${card.total} font-bold text-t1 tabular-nums`}>
                            {money(table.session_total)}
                          </span>
                        ) : null}
                      </div>
                      {tone === 'reserved' && next && (
                        <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-blue-700 dark:text-blue-300 tabular-nums">
                          <CalendarDays className="w-4 h-4 shrink-0" />{next.reservation_time.slice(0, 5)}{card.zone ? ` · ${next.party_size}` : ''}
                        </p>
                      )}
                      {seated && card.zone && <p className="mt-1 text-sm font-medium text-t1/80 truncate">{seated.customer_name.split(' ').slice(-1)[0]}</p>}
                      {card.zone && <p className="mt-1 text-sm text-t1/60 truncate">{table.zone_name}</p>}
                      {card.zone && settings?.guest_count_enabled === true && (
                        <p className="mt-1.5 flex items-center gap-1.5 text-base font-semibold text-t1/90">
                          <Users className="w-4 h-4" />
                          {table.current_session_id ? table.guest_count : table.capacity}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === 'alerts' && (
          <div className="p-3">
            {alerts.length === 0 ? (
              <EmptyState
                icon={Bell}
                title="Καμία ειδοποίηση"
                hint="Εδώ θα δεις έτοιμες παραγγελίες και καλέσματα."
              />
            ) : (
              <div className="space-y-2">
                {alerts.map((alert) => (
                  <div key={alert.id} className="role-mobile-card bg-surface-1 rounded-2xl p-3">
                    <p className="text-[15px] text-t1">{alert.message}</p>
                    <p className="text-[13px] text-t4 mt-0.5">
                      {alert.at.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'profile' && (
          <div className="p-3 space-y-3">
            <div className="role-mobile-card bg-surface-1 rounded-2xl p-3 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center shrink-0">
                <User className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-t1 text-base leading-tight truncate">
                  {employee?.first_name} {employee?.last_name}
                </p>
                <p className="text-sm text-t3 truncate">{store?.name}</p>
              </div>
            </div>

            <h2 className="text-sm font-semibold text-t3 uppercase tracking-wide px-1">
              Η μέρα μου
            </h2>
            {summaryLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
              </div>
            ) : summary ? (
              <div className="grid grid-cols-2 gap-2">
                {hasPermission('dine_in_view_registers') && (
                  <>
                    <div className="role-mobile-card bg-surface-1 rounded-2xl p-3">
                      <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{money(summary.cash_total)}</p>
                      <p className="text-sm text-t3">Μετρητά</p>
                    </div>
                    <div className="role-mobile-card bg-surface-1 rounded-2xl p-3">
                      <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{money(summary.card_total)}</p>
                      <p className="text-sm text-t3">Κάρτα</p>
                    </div>
                  </>
                )}
                <div className="role-mobile-card bg-surface-1 rounded-2xl p-3">
                  <p className="text-xl font-bold text-t1">{summary.orders_count ?? 0}</p>
                  <p className="text-sm text-t3">Παραγγελίες</p>
                </div>
                <div className="role-mobile-card bg-surface-1 rounded-2xl p-3">
                  <p className="text-xl font-bold text-t1">{summary.tables_served ?? 0}</p>
                  <p className="text-sm text-t3">Τραπέζια</p>
                </div>
              </div>
            ) : (
              <div className="role-mobile-card bg-surface-1 rounded-2xl p-3 text-center text-t3">
                Δεν υπάρχουν στοιχεία ακόμα.
              </div>
            )}

            <Button tone="danger" full onClick={() => employeeLogout()}>
              <LogOut className="w-5 h-5" />
              Αποσύνδεση
            </Button>
          </div>
        )}
      </main>

      <nav className="role-header role-bottom-nav fixed bottom-0 inset-x-0 z-40 bg-surface-1 border-t border-line flex wa-safe-bottom">
        {tabs.map(({ key, icon: Icon, label, badge }) => (
          <button aria-current={tab === key ? 'page' : undefined}
            key={key}
            onClick={() => {
              setTab(key);
              if (key === 'alerts') setUnread(0);
            }}
            className={`role-tab flex-1 min-h-[56px] flex flex-col items-center justify-center gap-0.5 relative ${
              tab === key ? 'text-cyan-600 dark:text-cyan-400' : 'text-t4'
            }`}
          >
            <Icon className="w-6 h-6" />
            <span className="text-[13px] font-semibold">{label}</span>
            {!!badge && badge > 0 && (
              <span className="absolute top-1 left-1/2 translate-x-3 min-w-[20px] h-[20px] px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                {badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {settingsOpen && <DeviceSettingsSheet onClose={() => setSettingsOpen(false)} />}

      {openTable && !ordering && (
        <TableSheet
          storeId={store!.id}
          table={openTable}
          reservation={tableReservations.get(openTable.id)?.seated ?? tableReservations.get(openTable.id)?.next ?? null}
          guestCountEnabled={settings?.guest_count_enabled === true}
          billPrintEnabled={settings?.bill_print_enabled === true}
          kitchenMessages={kitchenMessagePresets(settings)}
          canIssueReceipt={hasPermission('dine_in_print_receipt')}
          canTakePayment={hasPermission('dine_in_process_payment')}
          refreshSignal={refreshSignal}
          products={products}
          onClose={() => setOpenTable(null)}
          onChanged={loadTables}
          onNewOrder={startOrdering}
        />
      )}

      {openTable && ordering && (
        <OrderScreen
          storeId={store!.id}
          table={openTable}
          sessionId={ordering.sessionId}
          guestCount={ordering.guestCount}
          categories={dailyCatalog.categories}
          products={products}
          hiddenProductIds={dailyCatalog.hiddenProductIds}
          coursesEnabled={settings?.course_management_enabled === true}
          kitchenMessages={kitchenMessagePresets(settings)}
          onClose={() => setOrdering(null)}
          onCatalogStale={loadMenu}
          onSent={() => {
            setOrdering(null);
            setOpenTable(null);
            loadTables();
          }}
        />
      )}
    </div>
  );
};

export default WaiterApp;
